import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getTermsDocuments } from '../services/termsService';
import { TermsDocument } from '../types/terms';

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Read-only viewer for the Website Terms of Use (WTOU), opened from the
 * sidebar footer. The document is fetched lazily on first open and cached for
 * the lifetime of the component.
 */
const TermsModal: React.FC<TermsModalProps> = ({ open, onClose }) => {
  const [wtouDocument, setWtouDocument] = useState<TermsDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry so the fetch effect re-runs after a failure
  const [fetchAttempt, setFetchAttempt] = useState(0);

  useEffect(() => {
    if (!open || wtouDocument) {
      return;
    }

    let isMounted = true;
    const fetchTerms = async () => {
      try {
        setLoading(true);
        setError(null);
        const termsData = await getTermsDocuments();
        if (isMounted) {
          setWtouDocument(termsData?.wtou ?? null);
          if (!termsData?.wtou) {
            setError('Terms of Use are not available right now. Please try again later.');
          }
        }
      } catch (fetchError: any) {
        console.error('[TermsModal] Failed to load Terms of Use:', fetchError);
        if (isMounted) {
          setError(fetchError?.message || 'Failed to load the Terms of Use. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTerms();
    return () => {
      isMounted = false;
    };
  }, [open, wtouDocument, fetchAttempt]);

  const handleRetry = () => {
    setError(null);
    setWtouDocument(null);
    setFetchAttempt((attempt) => attempt + 1);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="terms-modal-title">
      <DialogTitle id="terms-modal-title">
        {wtouDocument?.title || 'Terms & Conditions'}
        {wtouDocument?.lastUpdated && (
          <Typography variant="caption" color="textSecondary" className="block">
            Last updated: {wtouDocument.lastUpdated}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && wtouDocument && (
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {(wtouDocument?.sections ?? []).length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No content available.
              </Typography>
            ) : (
              (wtouDocument?.sections ?? []).map((section, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {section?.heading && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>
                      {section.heading}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {section?.body}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsModal;
